import React, { JSX } from 'react';

declare global {
  interface Window {
    iconSymbolFile?: string;
    fallbackImage?: string;
  }
}

/**
 * Observe a media query from a CSS custom property or raw query string.
 */
export function observeMediaQuery(
  cssProperty: string,
  callback: (matches: boolean) => void,
  mqScope?: HTMLElement
) {
  const scope = mqScope ?? document.documentElement;
  const mqValue = getComputedStyle(scope).getPropertyValue(cssProperty);
  const query = mqValue === '' ? cssProperty : mqValue;
  const matchMedia = window.matchMedia(query);

  callback(matchMedia.matches);

  if (typeof matchMedia.addEventListener !== 'undefined') {
    matchMedia.addEventListener('change', () => callback(matchMedia.matches));
  } else {
    matchMedia.addListener(() => callback(matchMedia.matches)); // Legacy fallback
  }
}

/**
 * Get matchMedia result for a CSS custom property or raw query string.
 */
export function getMatchMedia(
  attributeValue: string,
  element: HTMLElement = document.documentElement
): boolean {
  if (attributeValue && attributeValue.startsWith('--')) {
    const mq = window
      .getComputedStyle(element)
      .getPropertyValue(attributeValue);
    return window.matchMedia(mq).matches;
  }
  return false;
}

/**
 * Log error if global icon path is not set.
 */
export function CheckIconPath() {
  if (!window.iconSymbolFile) {
    console.error(
      "Icons will not render correctly. You have to set a global variable 'window.iconSymbolFile' with the path of the svg symbol file."
    );
  }
}

/**
 * Return a React element for a SVG icon using <use> referencing a symbol.
 */
export function getSvgIcon(
  id: string,
  classes?: string,
  attributes?: React.SVGAttributes<SVGSVGElement>
): JSX.Element | null {
  if (!window.iconSymbolFile) return null;

  return (
    <svg aria-hidden="true" className={classes ?? ''} {...attributes}>
      <use xlinkHref={`${window.iconSymbolFile}#${id}`} />
    </svg>
  );
}

/**
 * Return raw HTML string of the SVG icon (non-React use).
 */
export function getSvgIconRaw(
  id: string,
  classes?: string,
  attributes: string = ''
): string {
  if (!window.iconSymbolFile) return '';

  return `<svg aria-hidden="true" class="${classes ?? ''}" ${attributes}>
        <use xlink:href="${window.iconSymbolFile}#${id}"></use>
    </svg>`;
}

/**
 * Return raw marker icon SVG string (non-React use).
 */
export function getMarkerIconSvg(attributes: string = ''): string {
  return `
<svg ${attributes} width="41" height="41" id="e3qSirTGXBQ1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 36 36" shape-rendering="geometricPrecision" text-rendering="geometricPrecision">
    <path id="marker-path" d="M34.8027,17.8152c0,9.3888-7.6111,17-17,17s-16.999966-7.6112-16.999966-17s7.611156-17.000014,16.999966-17.000014s17,7.611154,17,17.000014Z" fill="#fff" fill-opacity="0.1" stroke="#fff"/>
    <g id="marker-group" transform="translate(17.8028,17.815499) scale(1,1)">
        <g transform="translate(-17.8028,-17.815499)">
            <circle r="7.21275" transform="matrix(1 0 0-1 17.80255 17.81535)" fill="#fff"/>
            <circle r="9.32627" transform="translate(17.8028 17.8155)" fill="none" stroke="#fff"/>
        </g>
    </g>
</svg>
    `;
}

/**
 * Returns a React SVG element for a sort icon.
 */
export function getSortSvg(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      className="u-icon-micro"
      width="32"
      height="33"
      viewBox="0 0 32 33"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M15.5 30L7.27276 18.75H23.7272L15.5 30Z" />
      <path d="M15.5 3L23.7272 14.25H7.27276L15.5 3Z" />
    </svg>
  );
}

/**
 * Returns fallback image string set on the global window object.
 */
export function getFallbackImage(): string | undefined {
  return window.fallbackImage;
}

/**
 * Check if the current device supports touch input.
 */
export const isTouchDevice = (): boolean => {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0
  );
};
